import React from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, PlayCircle } from "lucide-react";
import { VIDEOS, LINKS, CHECKOUT_RADIOGRAFIA_49 } from "@/lib/content";

/**
 * Hero — full-bleed cinematic video with text + CTAs overlaid.
 * The hero video sits as the background of this section, with a layered
 * dark overlay (top→bottom) for readability of the headline and CTAs.
 */
const Hero = () => {
  const { scrollY } = useScroll();
  // Parallax: video drifts down slowly, text floats up slightly
  const videoY = useTransform(scrollY, [0, 700], [0, 140]);
  const textY = useTransform(scrollY, [0, 700], [0, -40]);
  const overlayOpacity = useTransform(scrollY, [0, 400], [1, 1.15]);

  return (
    <section
      data-testid="hero-section"
      className="relative w-full overflow-hidden"
      style={{ minHeight: "100vh" }}
    >
      {/* Background video layer */}
      <motion.div
        aria-hidden
        style={{ y: videoY }}
        className="absolute inset-0 z-0"
      >
        {/* Premium gradient fallback */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse at 30% 20%, rgba(197,160,89,0.18), transparent 55%), radial-gradient(ellipse at 80% 80%, rgba(19,40,61,0.95), transparent 65%), linear-gradient(135deg, #10202e 0%, #0c1a26 100%)",
          }}
        />
        <video
          src={VIDEOS.hero}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          data-testid="hero-video"
          className="absolute inset-0 w-full h-full object-cover"
        />
      </motion.div>

      {/* Layered dark overlays for readability */}
      <motion.div
        aria-hidden
        style={{ opacity: overlayOpacity }}
        className="absolute inset-0 z-10 pointer-events-none"
      >
        {/* Vertical gradient — darker at top (under navbar) and at bottom */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(16,32,46,0.85) 0%, rgba(16,32,46,0.55) 30%, rgba(16,32,46,0.55) 60%, rgba(16,32,46,0.95) 100%)",
          }}
        />
        {/* Left-side text-readability vignette */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(90deg, rgba(16,32,46,0.78) 0%, rgba(16,32,46,0.4) 45%, transparent 80%)",
          }}
        />
        {/* Top edge dark for navbar legibility */}
        <div
          className="absolute top-0 left-0 right-0 h-32"
          style={{
            background:
              "linear-gradient(180deg, rgba(16,32,46,0.9) 0%, transparent 100%)",
          }}
        />
      </motion.div>

      {/* Foreground content */}
      <motion.div
        style={{ y: textY }}
        className="relative z-20 et-container flex flex-col justify-center"
        // Vertically center within hero
      >
        <div className="pt-36 pb-24 md:pt-40 md:pb-32 max-w-3xl">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="et-eyebrow"
          >
            Radiografía de Negocio
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.12, ease: [0.22, 1, 0.36, 1] }}
            className="mt-6 text-5xl sm:text-6xl lg:text-7xl xl:text-[5.5rem] font-bold leading-[0.98] tracking-tight text-white et-text-balance"
            style={{ textShadow: "0 6px 40px rgba(0,0,0,0.45)" }}
          >
            Antes de automatizar,{" "}
            <span className="et-text-gold">ordena tu negocio.</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 22 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.22, ease: [0.22, 1, 0.36, 1] }}
            className="mt-7 text-lg md:text-xl text-[#f5f2ea]/95 max-w-2xl leading-relaxed"
          >
            Detecta en{" "}
            <span className="text-white font-semibold">20 minutos</span> dónde
            estás perdiendo tiempo, ventas y margen antes de meter más
            herramientas, IA o automatizaciones.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 22 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.36, ease: [0.22, 1, 0.36, 1] }}
            className="mt-9 flex flex-col sm:flex-row gap-3 sm:gap-4"
          >
            <a
              href={CHECKOUT_RADIOGRAFIA_49}
              target={CHECKOUT_RADIOGRAFIA_49.startsWith("http") ? "_top" : undefined}
              data-testid="hero-cta-primary"
              className="et-btn-primary !px-8 !py-4 !text-base shadow-2xl"
            >
              Hacer mi Radiografía por 49 €
              <ArrowRight className="w-5 h-5" />
            </a>
            <a
              href="#metodo"
              data-testid="hero-cta-secondary"
              className="et-btn-ghost !px-7 !py-4"
            >
              <PlayCircle className="w-5 h-5" />
              Ver el método
            </a>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.9, delay: 0.5 }}
            className="mt-5 text-sm md:text-base text-[#c5a059] font-medium"
          >
            Una prueba barata para decidir qué tocar primero.
          </motion.p>
        </div>
      </motion.div>

      {/* Bottom fade into next section */}
      <div
        aria-hidden
        className="absolute bottom-0 left-0 right-0 h-32 z-10 pointer-events-none"
        style={{
          background:
            "linear-gradient(180deg, transparent 0%, #10202e 100%)",
        }}
      />
    </section>
  );
};

export default Hero;
